<template>
  <div class="text-component">
    <IslandBadge type="text"></IslandBadge>
    <div class="component-contents">
      <div class="text-container">
        <TextEditor
          ref="textEditor"
          :value="state.text"
          :placeholder="'Enter a message...'"
          @text-updated="textUpdated"
          @error="setInvalidAttribute"
        ></TextEditor>
        <p v-if="isTextRequiredInvalid" class="help is-danger">Message text must not be empty</p>
        <p v-if="isTextAttributeInvalid" class="help is-danger">Message contains an invalid attribute</p>
      </div>
    </div>

    <div class="invalid-warning" v-if="state.invalid">
      <ErrorIcon class="icon-1-3x"></ErrorIcon>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { StateViewModel } from '@/features/bots/models/designer';
import TextEditor from '@/features/bots/components/designer/TextEditor/TextEditor.vue';
import IslandBadge from './IslandBadge.vue';
import { required } from '@vuelidate/validators';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import { computed, PropType, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  value: {
    type: Object as PropType<StateViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'state-updated', value: StateViewModel): void;
}>();

defineExpose({
  focusOnInput,
});

const state = ref<StateViewModel>({
  id: '',
  component: 'Text',
});
const invalidAttribute = ref(false);
const textEditor = ref<typeof TextEditor | null>(null);

const validations = {
  state: {
    text: {
      required,
      attributeInvalid: (value: string) => !invalidAttribute.value,
    },
  },
};

const $v = useVuelidate(validations, { state });
const isTextRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.state.text));
const isTextAttributeInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.state.text, 'attributeInvalid'),
);

watch(
  () => props.value,
  (value: StateViewModel) => (state.value = value),
  { immediate: true },
);

async function textUpdated(text: string) {
  if (state.value.text === text) {
    return;
  }
  state.value.text = text;

  $v.value.$touch();
  state.value.invalid = $v.value.$invalid;

  emit('state-updated', state.value);
}

function setInvalidAttribute(val: boolean) {
  invalidAttribute.value = val;
  $v.value.$touch();
  state.value.invalid = $v.value.$invalid;
}

function focusOnInput() {
  textEditor.value?.focusElement();
  textEditor.value?.startEditing();
}
</script>

<style scoped lang="scss">
.text-component {
  display: flex;
  flex: 1;
  border-radius: 5px;
}

.component-contents {
  padding-right: 2em;
}

.text-container {
  flex: 1;
}
</style>
